import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import Table from '../Table/Table';
import { transformDateCell, transformDateLongLocalizeCell } from '../../utils/cell';
import config from '../../config/config';
import ColorDollarAmountCell from '../Table/Cells/ColorDollarAmountCell/ColorDollarAmountCell';
import AmountFilter from '../Table/Filters/AmountFilter/AmountFilter';
import { getCurrencyCode } from '@nexio/emvio-util-app';
import ReserveActionFilter from '../Table/Filters/ReserveActionFilter/ReserveActionFilter';
import ReserveActivitiesListItem from './ReserveActivitiesListItem/ReserveActivitiesListItem';
import ReservesSummary from './ReservesSummary/ReservesSummary';
import ReserveMessage, { RESERVE_MESSAGE_MODAL_NAME } from './ReserveMessage/ReserveMessage';
import { RESERVE_MESSAGE_MIDS } from './ReserveMessage/reserveMessageMids';
import {
    getReserveActivities as fetchReserveActivities,
    hasSeenReserveMessageThisSession,
    markReserveMessageSeenThisSession
} from './reservesActions';
import {
    getReserveActivities,
    getCount,
    getPages,
    getReserveActivitiesErrorMessage,
    getIsReserveActivitiesLoading
} from './reservesEntity';
import { getSelf } from '../Self/selfEntity';
import { toggleModal } from '../Modal/modalActions';
import { selfShape } from '../../utils/propTypes';

// October 2026, midnight to midnight America/Denver (MDT, UTC-6; DST ends 2026-11-01).
export const RESERVE_MESSAGE_START = moment.utc('2026-10-01T06:00:00Z');
export const RESERVE_MESSAGE_END = moment.utc('2026-11-01T06:00:00Z');

const defaultColumnsAndProps = [
    {
        accessor: 'reportDate',
        Cell: transformDateCell,
        transformData: transformDateLongLocalizeCell,
        defaultSortDesc: true,
        filterable: false,
        label: 'Date',
        minWidth: 135,
        show: true
    },
    {
        accessor: 'merchantId',
        label: 'Merchant ID',
        minWidth: 145,
        filterable: false,
        show: false
    },
    {
        accessor: 'merchantName',
        filterable: false,
        label: 'Merchant',
        minWidth: 150,
        show: true,
        sortable: false
    },
    {
        accessor: 'amount',
        Cell: (props) => <ColorDollarAmountCell amount={props.value} currencyCode={getCurrencyCode(props.original.currencyId)} />,
        className: ['right-justify'],
        Filter: ({ filter, onChange }) => (<AmountFilter filter={filter} onChange={onChange} />),
        label: 'Amount',
        minWidth: 120,
        show: true
    },
    {
        accessor: 'action',
        Filter: ({ filter, onChange }) => (<ReserveActionFilter filter={filter} onChange={onChange} />),
        label: 'Action',
        minWidth: 165,
        show: true
    },
    {
        accessor: 'description',
        label: 'Description',
        filterable: false,
        sortable: false,
        minWidth: 200,
        show: true
    }
];

export class Reserves extends React.Component {
    static propTypes = {
        self: selfShape,
        onToggleReserveMessageModal: PropTypes.func.isRequired
    };

    componentDidMount() {
        this.checkForReserveMessage();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.self !== this.props.self) {
            this.checkForReserveMessage();
        }
    }

    checkForReserveMessage() {
        const merchantIds = Object.keys(get(this, 'props.self.accessRights.merchantIds', {}));
        const hasQualifyingMid = merchantIds.some((id) => RESERVE_MESSAGE_MIDS.has(String(id)));
        const inWindow = moment().isBetween(RESERVE_MESSAGE_START, RESERVE_MESSAGE_END, undefined, '[)');

        if (hasQualifyingMid && inWindow && !hasSeenReserveMessageThisSession()) {
            markReserveMessageSeenThisSession();
            this.props.onToggleReserveMessageModal();
        }
    }

    render() {
        const dateKey = 'reportDate';

        return (
            <div className="emvio-reserves">
                <ReserveMessage />
                <ReservesSummary />
                <Table
                    fetchUrl={config.reserveApiUrl}
                    className="emvio-reserve-activities-table"
                    dateKey={dateKey}
                    defaultColumnsAndProps={defaultColumnsAndProps}
                    defaultSorted={[{
                        desc: true,
                        id: dateKey
                    }]}
                    fetchData={fetchReserveActivities}
                    dataSelector={getReserveActivities}
                    countSelector={getCount}
                    errorMessageSelector={getReserveActivitiesErrorMessage}
                    isDataLoadingSelector={getIsReserveActivitiesLoading}
                    pagesSelector={getPages}
                    itemKey="id"
                    hasNoCount
                    noDataText="No Reserve Activities Found"
                    shouldUseGlobalDate
                    uniqueTableName="ReserveActivities"
                    ListItemComponent={ReserveActivitiesListItem}
                    filterable={true}
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        self: getSelf(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onToggleReserveMessageModal: () => dispatch(toggleModal(RESERVE_MESSAGE_MODAL_NAME))
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Reserves);
